// Self-contained client for documents-service (legal document governance).
// Mirrors billingApi.ts / hardwareApi.ts — SAME shared `platform` cluster,
// different base path (/documents).
//
// NOTE the host shape: this service is addressed by BASE PATH on the shared
// `platform` cluster, not by a per-service subdomain. Do not "correct" this to
// documents.api.<apex> — that pattern is closed (AWS 120-custom-domain cap).
//
// Two rules this file exists to protect:
//
//  1. STATUS IS DERIVED, NEVER STORED. The service returns `activeVersionNo`
//     plus per-version `isActive` / `isSuperseded` / `isScheduled`, computed
//     from the chain on every read. There is no stored "superseded" status and
//     nothing here may invent one.
//  2. `effectiveAt` is UTC midnight of a legal calendar day and is rendered in
//     UTC (see legalFormat.ts). Rendering it browser-local would show the
//     previous day for every US viewer.

import type {
  DocumentCategory,
  DocumentCategoryRecord,
  DocumentDischarge,
  ChangeType, DeltaDocument, DocumentBlock, DocumentKind, DocumentVersion, DocumentVisibility, Envelope,
  EnvelopeSigner, EnvelopeStatus, Folder, FolderColor, LegalDocument, MergeContext, VersionStatus,
} from '@platform/shared-types';

const detectStage = (): 'prod' | 'dev' | 'staging' => {
  try {
    const v = (import.meta as any)?.env?.VITE_STAGE;
    if (v === 'prod' || v === 'staging' || v === 'dev') return v;
  } catch { /* ignore */ }
  const h = typeof window !== 'undefined' ? window.location.hostname : '';
  if (h.startsWith('admin.dev.') || h.includes('localhost') || h.startsWith('127.')) return 'dev';
  if (h.includes('stage')) return 'staging';
  return 'prod';
};

/** Pure host resolver — exported for tests. */
export function resolveDocumentsHost(
  hostname: string,
  stage: 'prod' | 'staging' | 'dev',
  envBase?: string,
): string {
  if (envBase) return envBase;
  // Dev server proxies /documents-api → platform.api.<apiDevBase>/documents.
  if (hostname === 'localhost' || hostname === '127.0.0.1') return '/documents-api';
  const apex = hostname.replace(/^(dev|stage|www|admin\.dev|admin\.stage|admin)\./, '');
  if (stage === 'prod') return `https://platform.api.${apex}/documents`;
  if (stage === 'staging') return `https://platform.api.stage.${apex}/documents`;
  return `https://platform.api.dev.${apex}/documents`;
}

const host = (): string => {
  let envBase: string | undefined;
  try { envBase = (import.meta as any)?.env?.VITE_DOCUMENTS_API_BASE || undefined; } catch { /* ignore */ }
  const h = typeof window !== 'undefined' ? window.location.hostname : '';
  return resolveDocumentsHost(h, detectStage(), envBase);
};

/**
 * The admin Cognito ID token, exactly as every other client in this app sends
 * it. The route authorizers are COGNITO_USER_POOLS, whose identity source is
 * the raw `Authorization` header — the sibling clients (billing, hardware,
 * device-config, issues, academy) all send the bare token against this same
 * cluster, so this one does too.
 */
function authHeader(): Record<string, string> {
  const token = (typeof localStorage !== 'undefined' && localStorage.getItem('jwtToken')) || '';
  return token ? { Authorization: token } : {};
}

async function req<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`${host()}${path}`, {
    ...init,
    headers: { 'Content-Type': 'application/json', ...authHeader(), ...(init.headers || {}) },
  });
  if (!res.ok) {
    // Surface the service's own error code — `slug_already_exists`,
    // `version_not_editable`, `change_summary_required` and friends are the
    // most useful thing a user can be shown.
    let body: any = null;
    try { body = await res.json(); } catch { /* non-JSON body */ }
    const detail = body?.error || '';
    const err = new Error(detail || `documents ${res.status}`) as Error & { failures?: unknown };
    // `merge_fields_unresolvable` (publish gate, 409) carries a `failures`
    // array alongside `error` — see gateVersion() in documents-service. Every
    // other call site keeps reading `e.message` exactly as before; this is
    // additive so nothing downstream of `req()` needs to change.
    if (Array.isArray(body?.failures)) err.failures = body.failures;
    throw err;
  }
  return res.status === 204 ? (undefined as T) : res.json();
}

const q = (params: Record<string, string | number | undefined>): string => {
  const parts = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== '')
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`);
  return parts.length ? `?${parts.join('&')}` : '';
};

/* ── shapes returned by the service ─────────────────────────────────────── */

/**
 * One row of the version chain as `GET /admin/documents/{id}` returns it:
 * the stored version MINUS its blocks (they are fetched per version) PLUS the
 * three DERIVED booleans. `isSuperseded` and `isScheduled` are computed from
 * the chain on the server on every read — never read a stored equivalent,
 * there is none.
 */
/** A frozen artifact of a published version, with the URL to fetch it. */
export interface VersionArtifactRef {
  format: 'html' | 'pdf';
  sha256?: string;
  bytes?: number;
  rendererVersion?: string;
  renderedAt?: string;
  /** Public URL. Absent when the service could not build one. */
  url?: string;
}

export interface VersionSummary {
  documentId: string;
  versionNo: number;
  status: VersionStatus;
  effectiveAt?: string;
  changeType?: ChangeType;
  changeSummary?: string;
  createdBy: string;
  createdAt: string;
  approvedBy?: string;
  approvedAt?: string;
  selfApproved?: boolean;
  publishedBy?: string;
  publishedAt?: string;
  /**
   * A version sent back keeps its approval fields — it really was approved and
   * then withdrawn; blanking them would rewrite what happened. These three are
   * the LATEST rejection only; the full history is the version's `audit`.
   */
  rejectedBy?: string;
  rejectedAt?: string;
  rejectionReason?: string;
  /**
   * The acting user's email, snapshotted when they acted. Optional: versions
   * acted on before these were recorded have none, and the UI falls back to
   * the Cognito id.
   */
  createdByEmail?: string;
  submittedByEmail?: string;
  approvedByEmail?: string;
  publishedByEmail?: string;
  rejectedByEmail?: string;
  updatedAt?: string;
  blockCount: number;
  /** List surface only: true when the version has blocks OR Delta content. */
  hasContent?: boolean;
  isActive: boolean;
  isSuperseded: boolean;
  isScheduled: boolean;
}

/**
 * One row of a version's lifecycle timeline, as `GET .../versions/{n}` projects
 * it from the append-only audit table. Every field nullable because the
 * projection nulls each one. `action` is the stored spelling
 * (`version.submitted`, `version.rejected`, `version.approved`,
 * `version.published`, `version.draft_created`).
 */
export interface VersionAuditEntry {
  at: string | null;
  action: string | null;
  actorId: string | null;
  detail: Record<string, unknown> | null;
}

export interface DocumentDetail {
  document: LegalDocument;
  versions: VersionSummary[];
  /** null until something has actually taken effect. Derived, not stored. */
  activeVersionNo: number | null;
}

export interface BlastRadius {
  documentId: string;
  versionNo: number;
  changeType: ChangeType;
  effectiveAt: string;
  totalAcceptances: number;
  staleAcceptanceCount: number;
}

/**
 * ENVELOPES — one instance of a published `agreement` version addressed to
 * named signers. Three properties of these two routes are load-bearing and are
 * spelled out because they are NOT symmetrical:
 *
 *  1. THE LIST ROW IS A NARROW ALLOW-LIST, NOT THE ENVELOPE. `mergeData` is
 *     deliberately absent from it — a list of every device lease would
 *     otherwise carry every tenant's frozen contract terms into one response.
 *     `signers` is absent too, so a signer column costs one detail request per
 *     row. There is no signer count on the row to use instead.
 *
 *  2. THE TWO ROUTES DISAGREE ABOUT ABSENCE, and the types below say so rather
 *     than pretending otherwise. The list projection writes `?? null` for every
 *     optional field, so `sentAt`/`expiresAt`/`executedAt`/… arrive as `null`.
 *     The detail route returns the STORED ROW, written with
 *     `removeUndefinedValues`, so the same fields are simply MISSING there.
 *     `null` and `undefined` are both "no value", but code that tests
 *     `=== null` works on one route and silently fails on the other.
 *
 *  3. THE LIST IS ONE DYNAMODB PAGE, AND `templateDocumentId` FILTERS AFTER
 *     THE READ. The query fetches one page of the owner's envelopes newest
 *     first and the template filter is applied in memory afterwards, so a
 *     template's rows can be cut short by OTHER templates' envelopes filling
 *     the page. `count` is the number of rows returned — never a total. Any
 *     figure derived from this is "in the newest page", and the UI must say so.
 */
export interface EnvelopeRow {
  envelopeId: string;
  status: EnvelopeStatus;
  /** Frozen at creation. Never resolved to "latest", here or on the server. */
  templateDocumentId: string;
  templateVersionNo: number;
  tenantId: string | null;
  /** The process that opened it — `tenant_onboarding` + the tenant id, for a checkout. */
  contextType: string | null;
  contextRefId: string | null;
  createdBy: string;
  createdAt: string;
  sentAt: string | null;
  /**
   * Set at SEND, 30 days out by default — so a `draft` envelope has none. It is
   * a date and nothing more: no sweeper and no read-time derivation enforces
   * it yet, so a past `expiresAt` does not mean signing is blocked.
   */
  expiresAt: string | null;
  executedAt: string | null;
  declinedAt: string | null;
  voidedAt: string | null;
}

/**
 * One row of the append-only audit table, as the detail route projects it.
 * Every field is nullable because the projection nulls each one, and `action`
 * is the STORED spelling (`envelope.created`), not `EnvelopeEventSchema`'s
 * `type` (`created`) — the two shapes do not match yet and nothing here
 * pretends they do.
 */
export interface EnvelopeTimelineEntry {
  at: string | null;
  action: string | null;
  actorId: string | null;
  detail: Record<string, unknown> | null;
}

/**
 * `GET /admin/envelopes/{envelopeId}`. `envelope` is the stored row minus its
 * table keys — including the frozen `mergeData` the list route withholds.
 * Signers are their own rows and arrive alongside it; `envelope.signers` is
 * NOT populated by this route.
 */
export interface EnvelopeDetail {
  envelope: Envelope;
  signers: EnvelopeSigner[];
  events: EnvelopeTimelineEntry[];
}

export interface CreateFolderInput {
  name: string;
  parentFolderId?: string;
  sortOrder?: number;
  /** A palette id from `FOLDER_COLORS` — never a hex value. */
  color?: FolderColor;
}

export interface CreateDocumentInput {
  folderId: string;
  slug: string;
  title: string;
  kind: DocumentKind;
  /**
   * WHERE this document is offered — the axis pickers filter on, so a rental
   * agreement reaches the rent screen and nothing else does. Optional at the
   * API; the create forms ask for it because an uncategorised document appears
   * in no picker and is easy to lose.
   */
  category?: DocumentCategory;
  /**
   * WHAT the document is for — the subjects its merge fields are checked
   * against at publish. Optional: a document naming none keeps the gate's
   * permissive default, so nothing that exists today changes.
   */
  contexts?: MergeContext[];
  /**
   * HOW the reader discharges it — signed, or accept/decline. Optional: absent
   * means whatever the `kind` always implied (`dischargeOf`), so every document
   * written before this reads unchanged.
   */
  discharge?: DocumentDischarge;
}

export interface CreateCategoryInput {
  label: string;
  hint?: string;
  appliesTo?: 'agreement' | 'policy' | 'both';
  sortOrder?: number;
  /** Normally derived from `label`; passed only when an exact id is wanted. */
  categoryId?: string;
}

export interface PublishInput {
  effectiveAt: string;
  changeType: ChangeType;
  changeSummary?: string;
}

/**
 * One `(field, context)` pair the publish gate refused. Mirrors `GateFailure`
 * in documents-service's `lib/publishGate.ts` — `field` is exactly as written
 * in the document (before alias normalisation), `canonical` is what it
 * resolved to.
 */
export interface PublishGateFailure {
  field: string;
  canonical: string;
  context: string;
  reason: string;
}

/* ── the 15 /admin/* routes ─────────────────────────────────────────────── */

export const documentsApi = {
  /* folders */
  listFolders: () =>
    req<{ folders: Folder[]; count: number; invalid: string[] }>('/admin/folders'),

  createFolder: (input: CreateFolderInput) =>
    req<{ folder: Folder }>('/admin/folders', { method: 'POST', body: JSON.stringify(input) }),

  updateFolder: (folderId: string, patch: Partial<CreateFolderInput>) =>
    req<{ folder: Folder }>(`/admin/folders/${encodeURIComponent(folderId)}`, {
      method: 'PATCH', body: JSON.stringify(patch),
    }),

  /* categories — "where is this offered?", as rows rather than an enum.
     `list` seeds the built-ins on first read, so it never returns empty. */
  listCategories: (params: { kind?: DocumentKind; status?: 'active' } = {}) =>
    req<{ categories: DocumentCategoryRecord[]; count: number; invalid: string[] }>(
      `/admin/document-categories${q(params)}`,
    ),

  createCategory: (input: CreateCategoryInput) =>
    req<{ category: DocumentCategoryRecord }>('/admin/document-categories', {
      method: 'POST', body: JSON.stringify(input),
    }),

  /** `categoryId` is permanent — documents are filed under it. */
  updateCategory: (
    categoryId: string,
    patch: { label?: string; hint?: string; appliesTo?: 'agreement' | 'policy' | 'both'; sortOrder?: number; status?: 'active' | 'retired' },
  ) =>
    req<{ category: DocumentCategoryRecord }>(`/admin/document-categories/${encodeURIComponent(categoryId)}`, {
      method: 'PATCH', body: JSON.stringify(patch),
    }),

  /** Refused (409) for a built-in, or while any document is filed under it. */
  deleteCategory: (categoryId: string) =>
    req<{ deleted: boolean; categoryId: string }>(`/admin/document-categories/${encodeURIComponent(categoryId)}`, {
      method: 'DELETE',
    }),

  /* documents */
  listDocuments: (params: { folderId?: string; status?: 'active' | 'retired'; category?: string } = {}) =>
    req<{ documents: LegalDocument[]; count: number }>(`/admin/documents${q(params)}`),

  createDocument: (input: CreateDocumentInput) =>
    req<{ document: LegalDocument; version: DocumentVersion }>('/admin/documents', {
      method: 'POST', body: JSON.stringify(input),
    }),

  getDocument: (documentId: string) =>
    req<DocumentDetail>(`/admin/documents/${encodeURIComponent(documentId)}`),

  /**
   * `slug` is immutable — the service answers 400 `slug_is_immutable`.
   *
   * `folderId` and `category` are BOTH freely changeable, and they are
   * different questions: the folder is where an operator files it, the category
   * is where the product offers it. Filing a document in the wrong folder is a
   * tidiness problem; the category is what a picker reads.
   */
  updateDocument: (
    documentId: string,
    patch: {
      title?: string; folderId?: string; category?: DocumentCategory; contexts?: MergeContext[];
      /** Signed, or answered accept/decline. Applies to the NEXT envelope only. */
      discharge?: DocumentDischarge;
    },
  ) =>
    req<{ document: LegalDocument }>(`/admin/documents/${encodeURIComponent(documentId)}`, {
      method: 'PATCH', body: JSON.stringify(patch),
    }),

  /**
   * BRING AN ARCHIVED DOCUMENT BACK — the inverse of `retireDocument`, and the
   * one status value the PATCH route accepts. The service clears `retiredAt`
   * and `supersededByDocumentId` in the same write, because both describe a
   * withdrawal that has just been undone.
   *
   * The opposite direction is NOT this call: `status: 'retired'` is refused
   * with `use_retire_route`, so archiving keeps its own audit entry.
   */
  restoreDocument: (documentId: string) =>
    req<{ document: LegalDocument }>(`/admin/documents/${encodeURIComponent(documentId)}`, {
      method: 'PATCH', body: JSON.stringify({ status: 'active' }),
    }),

  retireDocument: (documentId: string, supersededByDocumentId?: string) =>
    req<{ document: LegalDocument; alreadyRetired?: boolean }>(
      `/admin/documents/${encodeURIComponent(documentId)}/retire`,
      { method: 'POST', body: JSON.stringify({ supersededByDocumentId }) },
    ),

  /* versions */
  createDraft: (documentId: string) =>
    req<{ version: DocumentVersion }>(
      `/admin/documents/${encodeURIComponent(documentId)}/versions`, { method: 'POST' },
    ),

  /**
   * `audit` is the version's lifecycle timeline (submitted / rejected with
   * reason / approved / published), chronological, from the append-only audit
   * table. `null` — as opposed to `[]` — means the history could not be read;
   * the two must render differently ("unavailable" vs "nothing yet").
   */
  getVersion: (documentId: string, versionNo: number) =>
    req<{ version: DocumentVersion; audit: VersionAuditEntry[] | null }>(
      `/admin/documents/${encodeURIComponent(documentId)}/versions/${versionNo}`,
    ),

  putBlocks: (documentId: string, versionNo: number, blocks: readonly DocumentBlock[]) =>
    req<{ version: DocumentVersion }>(
      `/admin/documents/${encodeURIComponent(documentId)}/versions/${versionNo}/blocks`,
      { method: 'PUT', body: JSON.stringify({ blocks }) },
    ),

  /**
   * Save the draft as ONE CONTINUOUS DOCUMENT.
   *
   * Same route as `putBlocks` on purpose — the body shape is what tells the
   * two apart, and documents-service shares a CloudFormation stack where a
   * second route would cost resources to express a difference the body
   * already carries. The service empties `blocks` in the same write, so a
   * version never ends up with two bodies that could disagree.
   */
  putDelta: (documentId: string, versionNo: number, delta: DeltaDocument) =>
    req<{ version: DocumentVersion }>(
      `/admin/documents/${encodeURIComponent(documentId)}/versions/${versionNo}/blocks`,
      { method: 'PUT', body: JSON.stringify({ delta }) },
    ),

  /* lifecycle */
  submit: (documentId: string, versionNo: number) =>
    req<{ versionNo: number; status: VersionStatus; submittedAt: string }>(
      `/admin/documents/${encodeURIComponent(documentId)}/versions/${versionNo}/submit`,
      { method: 'POST' },
    ),

  approve: (documentId: string, versionNo: number) =>
    req<{ versionNo: number; status: VersionStatus; approvedAt: string; selfApproved: boolean }>(
      `/admin/documents/${encodeURIComponent(documentId)}/versions/${versionNo}/approve`,
      { method: 'POST' },
    ),

  /**
   * Sends a version BACK to draft, from either `in_review` (a reviewer
   * refusing) or `approved` (withdrawing an approval before publish). One
   * endpoint for both — the state machine allows both edges, and the service
   * refuses everything else with 409 `invalid_transition`. The reason is
   * MANDATORY (whitespace counts as absent): a rejection with no reason is not
   * reviewable by the author or by anyone reading the audit trail later.
   */
  reject: (documentId: string, versionNo: number, reason: string) =>
    req<{ versionNo: number; status: VersionStatus; rejectedAt: string; reason: string }>(
      `/admin/documents/${encodeURIComponent(documentId)}/versions/${versionNo}/reject`,
      { method: 'POST', body: JSON.stringify({ reason }) },
    ),

  publish: (documentId: string, versionNo: number, input: PublishInput) =>
    req<{
      versionNo: number; status: VersionStatus; effectiveAt: string;
      changeType: ChangeType; changeSummary?: string; publishedAt: string;
    }>(
      `/admin/documents/${encodeURIComponent(documentId)}/versions/${versionNo}/publish`,
      { method: 'POST', body: JSON.stringify(input) },
    ),

  /**
   * How many acceptances go stale if THIS version is published with THAT
   * change type. `changeType` is required by the service — there is no default
   * anywhere in this flow — and `effectiveAt` moves the instant the projection
   * is evaluated at, so both belong in the query, not in a body.
   *
   * The count is NOT always 0 for `minor`: a party who accepted before an
   * earlier material version that has already taken effect is stale whatever
   * this version is. That is exactly why the publish screen must ask again
   * whenever the change type changes.
   */
  blastRadius: (
    documentId: string, versionNo: number, changeType: ChangeType, effectiveAt?: string,
  ) =>
    req<BlastRadius>(
      `/admin/documents/${encodeURIComponent(documentId)}/versions/${versionNo}/blast-radius${
        q({ changeType, effectiveAt })}`,
    ),

  /* envelopes */

  /**
   * Envelopes for one template, or all of them when `templateDocumentId` is
   * omitted. Read the EnvelopeRow docblock before deriving a figure from this:
   * the result is ONE DynamoDB page of the owner's envelopes with the template
   * filter applied afterwards, so it is a page, not a census.
   *
   * An unknown `status` is refused by the service (400 `invalid_status_filter`)
   * rather than ignored — a filter that silently did not apply would put an
   * executed agreement on a screen labelled "awaiting signature".
   *
   * `tenantId` IS THE EXCEPTION TO THE PAGE CAVEAT. It reads the per-tenant
   * partition (GSI2) rather than filtering the owner-wide page, so a
   * tenant-scoped listing is complete — which is what "every document signed
   * for this order" has to be to be worth showing.
   */
  listEnvelopes: (params: {
    templateDocumentId?: string; status?: EnvelopeStatus; tenantId?: string;
  } = {}) =>
    req<{ envelopes: EnvelopeRow[]; count: number }>(`/admin/envelopes${q(params)}`),

  /** The full envelope: frozen merge data, its signer rows, and its timeline. */
  getEnvelope: (envelopeId: string) =>
    req<EnvelopeDetail>(`/admin/envelopes/${encodeURIComponent(envelopeId)}`),

  /* visibility + share links (system documents, 2026-10-09) */

  /** Who can open the public link. Absent on the row ⇒ `public`. */
  setVisibility: (documentId: string, visibility: DocumentVisibility) =>
    req<{ document: LegalDocument }>(`/admin/documents/${encodeURIComponent(documentId)}`, {
      method: 'PATCH', body: JSON.stringify({ visibility }),
    }),

  listShares: (documentId: string) =>
    req<{ shares: DocumentShare[]; count: number }>(
      `/admin/documents/${encodeURIComponent(documentId)}/shares`,
    ),

  /** `expiresInHours` is > 0 and ≤ SHARE_LINK_MAX_TTL_HOURS (8760). */
  createShare: (documentId: string, input: { expiresInHours: number; note?: string }) =>
    req<{ share: DocumentShare; slug: string }>(
      `/admin/documents/${encodeURIComponent(documentId)}/shares`,
      { method: 'POST', body: JSON.stringify(input) },
    ),

  revokeShare: (documentId: string, token: string) =>
    req<{ share: DocumentShare }>(
      `/admin/documents/${encodeURIComponent(documentId)}/shares/${encodeURIComponent(token)}/revoke`,
      { method: 'POST' },
    ),
};

/** One private share link, as documents-service projects it. */
export interface DocumentShare {
  token: string;
  documentId: string;
  createdAt: string;
  createdBy?: string;
  createdByEmail?: string;
  expiresAt: string;
  note?: string;
  revokedAt?: string | null;
  /** Derived server-side: not revoked and not yet expired. */
  active: boolean;
}

/**
 * The platform-app public origin that serves `/#/legal/<slug>` — dev.rubytill.com,
 * stage.rubytill.com or rubytill.com. Same stage/apex derivation as the API host.
 */
export function resolvePublicSite(
  hostname: string,
  stage: 'prod' | 'staging' | 'dev',
): string {
  const local = hostname === 'localhost' || hostname === '127.0.0.1' || !hostname.includes('.');
  const apex = local ? 'rubytill.com' : hostname.replace(/^(dev|stage|www|admin\.dev|admin\.stage|admin)\./, '');
  if (stage === 'prod') return `https://${apex}`;
  if (stage === 'staging') return `https://stage.${apex}`;
  return `https://dev.${apex}`;
}

export const publicSite = (): string => {
  const h = typeof window !== 'undefined' ? window.location.hostname : '';
  return resolvePublicSite(h, detectStage());
};

/** Permanent public link — always shows the version in force. */
export const publicDocumentUrl = (slug: string): string => `${publicSite()}/#/legal/${slug}`;

/** Private share link — the permanent link plus a share token. */
export const shareDocumentUrl = (slug: string, token: string): string =>
  `${publicDocumentUrl(slug)}?share=${encodeURIComponent(token)}`;

export default documentsApi;
